<template>
  <div class="hidden grow gap-4 md:flex">
    <JobSearchInput v-model="searchString" />
    <JobFilterSelect
      v-model="category"
      :filterOptions="filterOptions.category"
      :filterTypeLabel="$t('jobPlatform.filterIndustey')"
    />
    <JobFilterSelect
      v-model="location"
      :filterOptions="filterOptions.location"
      :filterTypeLabel="$t('jobPlatform.filterLocation')"
    />
  </div>
  <div
    v-if="mobileFilterSelectActive"
    class="flex flex-col fixed top-0 left-0 right-0 z-[10000] w-full overflow-x-hidden overflow-y-auto h-[100%] max-h-full bg-white"
  >
    <div class="flex pt-3 pb-3 pl-2 pr-2 border-b border-b-gray-300">
      <div
        class="flex flex-grow justify-center font-sans leading-[52px] h-[52px] text-xl font-semibold relative"
      >
        {{ $t('jobPlatform.mobileFilterModalHeader') }}
      </div>
      <CommonButton
        size="xxlarge"
        bg="bg-white"
        @click="toggleMobileFilterModal(false)"
        class="min-w-[52px] absolute right-2"
      >
        <CommonFontAwesomeIcon icon="fa-regular fa-xmark-large" />
      </CommonButton>
    </div>
    <div class="flex-col flex-grow font-sans pl-6 pr-6 overflow-auto">
      <div class="text-sm font-semibold pt-8 pb-6">
        {{ $t('jobPlatform.filterIndustey') }}
      </div>
      <div class="flex flex-col gap-4">
        <label
          v-for="option in filterOptions.category"
          class="flex cursor-pointer leading-[30px]"
        >
          <FormCheckbox
            :checked="
              tempFilterData.category &&
              tempFilterData.category.indexOf(option) !== -1
            "
            @update:checked="setTempFilter('category', option)"
          />
          {{ option }}
        </label>
      </div>
      <div class="text-sm font-semibold pt-8 pb-6">
        {{ $t('jobPlatform.filterLocation') }}
      </div>
      <div class="flex flex-col gap-4">
        <label
          v-for="option in filterOptions.location"
          class="flex cursor-pointer leading-[30px]"
        >
          <FormCheckbox
            :checked="
              tempFilterData.location &&
              tempFilterData.location.indexOf(option) !== -1
            "
            @update:checked="setTempFilter('location', option)"
          />
          {{ option }}
        </label>
      </div>
    </div>
    <div
      class="flex h-18 pt-3 pb-3 pl-2 pr-2 gap-2 font-sans relative border-t border-b-gray-300"
    >
      <CommonButton
        size="xxlarge"
        bg="bg-white"
        @click="
          () => {
            clearFilters()
            toggleMobileFilterModal(false)
          }
        "
        class="min-w-[52px] flex-grow"
      >
        {{ $t('jobPlatform.clearFilters') }}
      </CommonButton>
      <CommonButton
        size="xxlarge"
        bg="bg-blue"
        text="text-white text-lg"
        @click="
          () => {
            confirmFilterSelection()
            toggleMobileFilterModal(false)
          }
        "
        class="min-w-[52px] flex-grow text-lg"
      >
        {{ $t('jobPlatform.addFilters') }}
        <div
          v-if="totalSetTempFilters > 0"
          class="ml-2 h-6 mt-1 leading-6 rounded-3xl text-white text-center min-w-[24px] bg-[#ffffff24]"
        >
          {{ totalSetTempFilters }}
        </div>
      </CommonButton>
    </div>
  </div>
  <div class="flex md:hidden gap-2">
    <JobSearchInput v-model="searchString" />
    <CommonButton
      size="xxlarge"
      bg="bg-blue"
      text="text-white"
      @click="toggleMobileFilterModal(true)"
      class="w-[52px]"
    >
      <CommonFontAwesomeIcon :icon="'fa-solid fa-bars-filter'" />
      <div
        v-if="totalActiveFilters > 0"
        class="ml-2 l-1 pr-1 h-6 mt-1 leading-[22px] rounded-3xl text-white text-center"
      >
        {{ totalActiveFilters }}
      </div>
    </CommonButton>
  </div>
</template>

<script setup lang="ts">
import type { FilterChangeEvent } from '~/typesManual/jobTypes'

const props = defineProps<{
  activeFilters: { [id: string]: string | string[] }
  filterOptions: { [id: string]: string[] }
}>()

const totalActiveFilters = computed<number>(() => {
  const locationCount = location.value ? location.value.length : 0
  const categoryCount = category.value ? category.value.length : 0

  return locationCount + categoryCount
})

const totalSetTempFilters = computed<number>(() => {
  const locationCount = tempFilterData.value.location
    ? tempFilterData.value.location.length
    : 0
  const categoryCount = tempFilterData.value.category
    ? tempFilterData.value.category.length
    : 0

  return locationCount + categoryCount
})

const searchString = computed<string>({
  get: () => {
    return props.activeFilters.searchString as string
  },
  set: (newValue) =>
    emit('setChange', {
      searchString: newValue,
      category: category.value ? category.value : [],
      location: location.value ? location.value : [],
    }),
})

const category = computed<string[]>({
  get: () => {
    return props.activeFilters.category as string[]
  },
  set: (newValue) => {
    emit('setChange', {
      searchString: searchString.value ? searchString.value : '',
      category: newValue,
      location: location.value ? location.value : [],
    })
  },
})

const location = computed<string[]>({
  get: () => {
    return props.activeFilters.location as string[]
  },
  set: (newValue) => {
    emit('setChange', {
      searchString: searchString.value ? searchString.value : '',
      category: category.value ? category.value : [],
      location: newValue,
    })
  },
})

const tempFilterData = ref<{ [id: string]: string[] }>({})
const mobileFilterSelectActive = ref(false)

const toggleMobileFilterModal = (toggleState: boolean) => {
  if (toggleState) {
    tempFilterData.value = {
      category: category.value ? category.value : [],
      location: location.value ? location.value : [],
    }
  } else {
    tempFilterData.value = {}
  }

  mobileFilterSelectActive.value = toggleState
}

// const hasFilterFlag = computed<boolean>(() => {
//   if (searchString.value && searchString.value !== '') {
//     return true
//   } else if (category.value && category.value.length > 0) {
//     return true
//   } else if (location.value && location.value.length > 0) {
//     return true
//   }

//   return false
// })

const setTempFilter = (filterType: string, filterLabel: string) => {
  const typeBucket = tempFilterData.value[filterType]
  if (typeBucket.indexOf(filterLabel) !== -1) {
    typeBucket.splice(typeBucket.indexOf(filterLabel), 1)
  } else {
    typeBucket.push(filterLabel)
  }
}
const confirmFilterSelection = () => {
  emit('setChange', {
    searchString: searchString.value ? searchString.value : '',
    category: tempFilterData.value.category
      ? tempFilterData.value.category
      : [],
    location: tempFilterData.value.location
      ? tempFilterData.value.location
      : [],
  })
}

const clearFilters = () => {
  emit('setChange', {
    searchString: '',
    category: [],
    location: [],
  })
}

const emit = defineEmits<{
  (e: 'setChange', value: FilterChangeEvent): void
}>()
</script>
